import { CreditNoteSortableColumns, type SortableColumn } from '@/models/creditNoteSorting';
import usePaginatorStore, { PAGINATOR_KEYS } from '@/stores/usePaginatorStore';
import { type DateRange, type SortState, cloneDateRange } from '@/utils/localStorageUtil';
import { startOfDay } from 'date-fns';
import { defineStore } from 'pinia';
import type { DataTableSortEvent } from 'primevue/datatable';
import { computed, ref, watch } from 'vue';

type CreditNoteTransferredSortState = SortState<CreditNoteSortableColumns>;
type CreditNoteDateRange = DateRange;

const isCreditNoteSortableColumn = (value: unknown): value is SortableColumn => {
    return (
        value === CreditNoteSortableColumns.CreationDate ||
        value === CreditNoteSortableColumns.PartnerName ||
        value === CreditNoteSortableColumns.CreditNoteNumber ||
        value === CreditNoteSortableColumns.ClientName
    );
};

const DEFAULT_SORT_STATE: CreditNoteTransferredSortState = {
    columnName: CreditNoteSortableColumns.CreationDate,
    columnOrder: -1,
};

const createDefaultCreditNoteDateRange = (): CreditNoteDateRange => {
    const now = startOfDay(new Date());
    return [new Date(now), new Date(now)];
};

const DEFAULT_FILTER_DATE_RANGE = createDefaultCreditNoteDateRange();

const useCreditNoteStore = defineStore(
    'creditNote',
    () => {
        const paginatorStore = usePaginatorStore();
        const { pageIndexRef: pageIndex } = paginatorStore.getPaginator(
            PAGINATOR_KEYS.CREDIT_NOTES
        );

        const defaultDateRange = ref<CreditNoteDateRange>(
            cloneDateRange(DEFAULT_FILTER_DATE_RANGE)
        );
        const filterDateRange = ref<CreditNoteDateRange>(cloneDateRange(defaultDateRange.value));
        const filterClientIds = ref<number[]>([]);
        const filterAutomaticProcessedOnly = ref(false);
        const clientFilterRef = ref<{ hide?: () => void; overlayVisible?: boolean } | null>(null);

        const sortState = ref<CreditNoteTransferredSortState>({
            ...DEFAULT_SORT_STATE,
        });

        const resetCreditNoteTransferredPageIndex = () => {
            if (pageIndex.value !== 0) {
                pageIndex.value = 0;
            }
        };

        const setCreditNoteTransferredSortState = (nextState: CreditNoteTransferredSortState) => {
            if (
                sortState.value.columnName === nextState.columnName &&
                sortState.value.columnOrder === nextState.columnOrder
            ) {
                return;
            }

            sortState.value = nextState;
        };

        const tableSortChange = (event: DataTableSortEvent): void => {
            const columnName = isCreditNoteSortableColumn(event.sortField)
                ? event.sortField
                : DEFAULT_SORT_STATE.columnName;
            const columnOrder = event.sortOrder === 1 ? 1 : -1;

            setCreditNoteTransferredSortState({
                columnName,
                columnOrder,
            });
        };

        const selectedColumn = computed({
            get: () => sortState.value.columnName,
            set: (value: SortableColumn) => {
                setCreditNoteTransferredSortState({
                    columnName: value,
                    columnOrder: sortState.value.columnOrder,
                });
            },
        });

        const isDescending = computed({
            get: () => sortState.value.columnOrder > 0,
            set: (value: boolean) => {
                setCreditNoteTransferredSortState({
                    columnName: sortState.value.columnName,
                    columnOrder: value ? 1 : -1,
                });
            },
        });

        const creditNoteSortByApiColumn = computed<CreditNoteSortableColumns>(
            () => sortState.value.columnName
        );

        const creditNoteSortByDescending = computed(() => sortState.value.columnOrder !== 1);

        type DateRangeTuple = [Date, Date];

        const isValidDate = (value: unknown): value is Date =>
            (value instanceof Date ||
                (typeof value === 'string' && new Date(value) instanceof Date)) &&
            !Number.isNaN(new Date(value).getTime());

        const isCompleteDateRange = (value: unknown): value is DateRangeTuple => {
            return (
                Array.isArray(value) &&
                value.length === 2 &&
                isValidDate(value[0]) &&
                isValidDate(value[1])
            );
        };

        watch(
            filterDateRange,
            () => {
                resetCreditNoteTransferredPageIndex();
            },
            { deep: true }
        );

        watch(
            filterClientIds,
            () => {
                resetCreditNoteTransferredPageIndex();
            },
            { deep: true }
        );

        watch(filterAutomaticProcessedOnly, () => {
            resetCreditNoteTransferredPageIndex();
        });

        return {
            tableSortChange,
            isCompleteDateRange,
            sortState,
            creditNoteSortByApiColumn,
            creditNoteSortByDescending,
            selectedColumn,
            isDescending,
            filterDateRange,
            defaultDateRange,
            filterClientIds,
            filterAutomaticProcessedOnly,
            clientFilterRef,
        };
    },
    {
        persist: [
            {
                storage: localStorage,
                pick: ['sortState'],
                key: 'cnss',
            },
            {
                storage: localStorage,
                pick: ['filterDateRange'],
                key: 'cndr',
            },
            {
                storage: localStorage,
                pick: ['filterClientIds'],
                key: 'cnci',
            },
            {
                storage: localStorage,
                pick: ['filterAutomaticProcessedOnly'],
                key: 'cnap',
            },
            {
                storage: localStorage,
                pick: ['isDescending'],
                key: 'cnid',
            },
        ],
    }
);

export default useCreditNoteStore;
